import React, { useState, useEffect } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  Link,
} from "react-router-dom";
import { useAuth, useUser } from "@clerk/clerk-react";
import { useBuilding } from "../../context/BuildingContext";
import { unitsApi, buildingsApi } from "../../services/api";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import PageHeader from "../../components/common/PageHeader";
import {
  Building,
  Contact,
  ResidentRole,
  StatementStatus,
  Unit,
  UnitResident,
  UnitStatement,
  UnitStatus,
} from "@akfdev-com/shared/src/models";
import { contactsApi } from "../../services/api";

const UnitDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getToken } = useAuth();
  const { user } = useUser();
  const { currentBuilding } = useBuilding();
  const buildingIdFromQuery =
    searchParams.get("building") || currentBuilding?.id || "";

  const [unit, setUnit] = useState<Unit | null>(null);
  // Parent building carries the fallback Management Start Date used by
  // the Add Resident modal + the read-only inherited-date hint (Bucket B
  // #6 / #7). Fetched alongside the unit on load; null when we don't have
  // it or the parent isn't visible to this owner.
  const [parentBuilding, setParentBuilding] = useState<Building | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Unit>>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddResidentDialog, setShowAddResidentDialog] = useState(false);
  const [residentContacts, setResidentContacts] = useState<Contact[]>([]);
  const [addResidentForm, setAddResidentForm] = useState({
    contactId: "",
    isPrimary: false,
    moveInDate: new Date().toISOString().split("T")[0],
  });
  const [selectedResidentId, setSelectedResidentId] = useState<string | null>(
    null,
  );
  const [showRemoveResidentConfirm, setShowRemoveResidentConfirm] =
    useState(false);

  useEffect(() => {
    const fetchUnitDetails = async () => {
      if (!id || id === "new") {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await unitsApi.get(id);
        if (!data || (data as any).unit === null) {
          setUnit(null);
          return;
        }
        const loadedUnit = data as Unit;
        setUnit(loadedUnit);
        setEditForm(loadedUnit);
        // Load the parent building so the Add Resident modal can default
        // moveInDate to the effective management start date (Bucket B #7)
        // and the Management Start Date field can show its fallback hint.
        // Non-fatal on failure — the modal falls back to today.
        if (loadedUnit.buildingId) {
          try {
            const b = await buildingsApi.get(loadedUnit.buildingId);
            setParentBuilding(b as Building);
          } catch (err) {
            console.warn(
              "[UnitDetails] failed to load parent building for defaults:",
              err,
            );
          }
        }
      } catch (err) {
        console.error("Error fetching unit details:", err);
        setError("Failed to load unit details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchUnitDetails();
  }, [id]);

  /**
   * Effective Management Start Date for this unit — unit's own value →
   * parent building's → today. Same precedence the backend applies when
   * classifying residents on import. Returns an ISO YYYY-MM-DD string.
   */
  const effectiveManagementStartDate = React.useMemo(() => {
    const u = unit?.managementStartDate;
    if (typeof u === "string" && u.length > 0) return u.slice(0, 10);
    const b = parentBuilding?.managementStartDate;
    if (typeof b === "string" && b.length > 0) return b.slice(0, 10);
    return new Date().toISOString().split("T")[0];
  }, [unit?.managementStartDate, parentBuilding?.managementStartDate]);

  const handleSaveChanges = async () => {
    if (!unit || !id) return;

    try {
      setLoading(true);
      const result = await unitsApi.update(id, editForm);
      const updated =
        (result?.unit as Unit) || ({ ...unit, ...editForm } as Unit);
      setUnit(updated);
      setEditForm(updated);
      setIsEditing(false);
    } catch (err) {
      console.error("Error updating unit:", err);
      setError("Failed to update unit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUnit = async () => {
    if (!id) return;

    try {
      setLoading(true);
      await unitsApi.delete(id);
      const returnTo = unit?.buildingId
        ? `/dashboard/buildings/${unit.buildingId}?tab=units`
        : "/dashboard/units";
      navigate(returnTo);
    } catch (err) {
      console.error("Error deleting unit:", err);
      setError("Failed to delete unit. Please try again.");
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleCreateUnit = async () => {
    if (!editForm.name || !buildingIdFromQuery) return;
    setLoading(true);
    setError(null);
    try {
      const ownerId =
        (user?.publicMetadata?.ownerId as string) || user?.id || "";
      await unitsApi.create({
        ...editForm,
        buildingId: buildingIdFromQuery,
        ownerId,
        status: editForm.status || UnitStatus.Vacant,
        residents: [],
        recentStatements: [],
      });
      navigate(`/dashboard/buildings/${buildingIdFromQuery}?tab=units`);
    } catch (err) {
      console.error("Error creating unit:", err);
      setError("Failed to create unit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveResident = async () => {
    if (!unit || !id || !selectedResidentId) return;

    try {
      setLoading(true);
      const token = (await getToken()) || "";
      // In a real app, you would update via API
      // await fetch(`/api/units/${id}/residents/${selectedResidentId}`, {
      //   method: 'DELETE',
      //   headers: { Authorization: `Bearer ${token}` }
      // });

      // For demonstration, we'll just update the local state
      const moveOutDate = new Date().toISOString().split("T")[0];
      const updatedResidents = unit.residents.map((resident) =>
        resident.id === selectedResidentId
          ? { ...resident, moveOutDate }
          : resident,
      );
      const activeResidents = updatedResidents.filter((r) => !r.moveOutDate);

      const updatedUnit = {
        ...unit,
        residents: updatedResidents,
        status:
          activeResidents.length > 0 ? UnitStatus.Occupied : UnitStatus.Vacant,
      };
      await unitsApi.update(id, {
        residents: updatedResidents,
        status: updatedUnit.status,
        buildingId: unit.buildingId,
      });
      setUnit(updatedUnit);
    } catch (err) {
      console.error("Error removing resident:", err);
      setError("Failed to remove resident. Please try again.");
    } finally {
      setLoading(false);
      setShowRemoveResidentConfirm(false);
      setSelectedResidentId(null);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    if (
      name === "size" ||
      name === "bedrooms" ||
      name === "bathrooms" ||
      name === "shareNumerator" ||
      name === "shareDenominator"
    ) {
      setEditForm((prev) => ({ ...prev, [name]: parseInt(value, 10) || 0 }));
    } else {
      setEditForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Load resident contacts when the Add Resident dialog opens
  const handleOpenAddResidentDialog = async () => {
    try {
      const all: Contact[] = await contactsApi.getAll();
      const assignedContactIds = new Set(
        (unit?.residents ?? [])
          .filter((r) => !r.moveOutDate)
          .map((r) => r.contactId),
      );
      setResidentContacts(
        all.filter(
          (c) =>
            c.type === "resident" &&
            !c.archivedAt &&
            !assignedContactIds.has(c.id),
        ),
      );
    } catch {
      setResidentContacts([]);
    }
    // Default the moveInDate to the effective management start date
    // rather than today (Bucket B #3c / #7). Treasurer can still override
    // to any date they want. Reset every open so a previously-typed value
    // doesn't leak into the next add.
    setAddResidentForm({
      contactId: "",
      isPrimary: false,
      moveInDate: effectiveManagementStartDate,
    });
    setShowAddResidentDialog(true);
  };

  // Add Resident dialog handlers
  const handleAddResidentFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setAddResidentForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAddResident = async () => {
    if (!unit || !id || !addResidentForm.contactId) return;
    const contact = residentContacts.find(
      (c) => c.id === addResidentForm.contactId,
    );
    if (!contact) return;
    const newResident: UnitResident = {
      id: Math.random().toString(36).slice(2),
      contactId: addResidentForm.contactId,
      name: contact.name,
      email: contact.email ?? "",
      phone: contact.phone,
      isPrimary: addResidentForm.isPrimary,
      role: addResidentForm.isPrimary
        ? ResidentRole.Primary
        : ResidentRole.Secondary,
      moveInDate: addResidentForm.moveInDate,
    };
    const updatedResidents = [...unit.residents, newResident];
    const updatedUnit = {
      ...unit,
      residents: updatedResidents,
      status: UnitStatus.Occupied,
    };
    await unitsApi.update(id, {
      residents: updatedResidents,
      status: updatedUnit.status,
      buildingId: unit.buildingId,
    });
    setUnit(updatedUnit);
    setShowAddResidentDialog(false);
    setAddResidentForm({
      contactId: "",
      isPrimary: false,
      moveInDate: new Date().toISOString().split("T")[0],
    });
  };

  if (loading && !unit && id !== "new") {
    return <Spinner message="Loading unit details..." />;
  }

  if (error && id !== "new") {
    return <ErrorAlert message={error} />;
  }

  if (id === "new") {
    return (
      <div className="">
        <PageHeader
          title="New Unit"
          description={`Adding a unit to ${
            currentBuilding?.name || "your building"
          }`}
          actions={
            <div className="flex space-x-2">
              <button
                onClick={() =>
                  navigate(
                    `/dashboard/buildings/${buildingIdFromQuery}?tab=units`,
                  )
                }
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUnit}
                disabled={loading || !editForm.name}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none disabled:opacity-50"
              >
                Save Unit
              </button>
            </div>
          }
        />
        {error && (
          <div className="mt-4">
            <ErrorAlert message={error} />
          </div>
        )}
        <div className="mt-6 bg-white shadow rounded-lg">
          <div className="p-6">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div className="sm:col-span-3">
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Unit Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  required
                  value={editForm.name || ""}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="e.g. Flat 1A"
                />
              </div>
              <div className="sm:col-span-3">
                <label
                  htmlFor="floor"
                  className="block text-sm font-medium text-gray-700"
                >
                  Floor
                </label>
                <input
                  type="text"
                  name="floor"
                  id="floor"
                  value={editForm.floor || ""}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="e.g. Ground, First, Second"
                />
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="size"
                  className="block text-sm font-medium text-gray-700"
                >
                  Size (m²)
                </label>
                <input
                  type="number"
                  name="size"
                  id="size"
                  min="0"
                  step="0.01"
                  value={editForm.size || ""}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="bedrooms"
                  className="block text-sm font-medium text-gray-700"
                >
                  Bedrooms
                </label>
                <input
                  type="number"
                  name="bedrooms"
                  id="bedrooms"
                  min="0"
                  step="1"
                  value={editForm.bedrooms || ""}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="bathrooms"
                  className="block text-sm font-medium text-gray-700"
                >
                  Bathrooms
                </label>
                <input
                  type="number"
                  name="bathrooms"
                  id="bathrooms"
                  min="0"
                  step="0.5"
                  value={editForm.bathrooms || ""}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
              <div className="sm:col-span-3">
                <label
                  htmlFor="shareNumerator"
                  className="block text-sm font-medium text-gray-700"
                >
                  Service Charge Share <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500">
                  Enter as numerator/denominator, e.g. 1 / 12
                </p>
                <div className="flex items-center space-x-2 mt-1">
                  <input
                    type="number"
                    name="shareNumerator"
                    id="shareNumerator"
                    value={editForm.shareNumerator ?? 1}
                    onChange={handleInputChange}
                    className="block w-20 border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    min="1"
                    step="1"
                    aria-label="Numerator"
                  />
                  <span className="text-gray-500 font-medium">/</span>
                  <input
                    type="number"
                    name="shareDenominator"
                    id="shareDenominator"
                    value={editForm.shareDenominator ?? 1}
                    onChange={handleInputChange}
                    className="block w-20 border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    min="1"
                    step="1"
                    aria-label="Denominator"
                  />
                </div>
              </div>
              <div className="sm:col-span-3">
                <label
                  htmlFor="status"
                  className="block text-sm font-medium text-gray-700"
                >
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={editForm.status || UnitStatus.Vacant}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                >
                  <option value="vacant">Vacant</option>
                  <option value="occupied">Occupied</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div className="sm:col-span-6">
                <label
                  htmlFor="notes"
                  className="block text-sm font-medium text-gray-700"
                >
                  Notes
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  rows={3}
                  value={editForm.notes || ""}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!unit) {
    return <ErrorAlert message="Unit not found" />;
  }

  const isOccupied = unit.status === UnitStatus.Occupied;
  const isPending = unit.status === "pending";
  const isVacant = unit.status === "vacant";

  return (
    <div className="">
      <PageHeader
        title={unit.name}
        description={`${unit.floor ? `${unit.floor} floor • ` : ""}${
          unit.size ? `${unit.size} m² • ` : ""
        }share: ${unit.shareNumerator}/${unit.shareDenominator}`}
        actions={
          <div className="flex space-x-2">
            {isEditing ? (
              <>
                <button
                  onClick={() => {
                    setEditForm(unit);
                    setIsEditing(false);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveChanges}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none"
                >
                  Save Changes
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                >
                  Edit
                </button>
                <Link
                  to={`/dashboard/statements/new?unitId=${unit.id}`}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none"
                >
                  Create Statement
                </Link>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        }
      />

      {/* Unit Status Banner */}
      <div
        className={`mt-6 px-4 py-3 rounded-md ${
          isOccupied
            ? "bg-green-50 border border-green-200"
            : isPending
            ? "bg-yellow-50 border border-yellow-200"
            : "bg-gray-50 border border-gray-200"
        }`}
      >
        <div className="flex">
          <div className="flex-shrink-0">
            {isOccupied ? (
              <svg
                className="h-5 w-5 text-green-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            ) : isPending ? (
              <svg
                className="h-5 w-5 text-yellow-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg
                className="h-5 w-5 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
              </svg>
            )}
          </div>
          <div className="ml-3">
            <h3
              className={`text-sm font-medium ${
                isOccupied
                  ? "text-green-800"
                  : isPending
                  ? "text-yellow-800"
                  : "text-gray-800"
              }`}
            >
              {isOccupied
                ? "Unit Occupied"
                : isPending
                ? "Pending Occupancy"
                : "Unit Vacant"}
            </h3>
            <div className="mt-2 text-sm">
              <p
                className={
                  isOccupied
                    ? "text-green-700"
                    : isPending
                    ? "text-yellow-700"
                    : "text-gray-700"
                }
              >
                {isOccupied
                  ? `This unit has ${
                      unit.residents.filter((r) => !r.moveOutDate).length
                    } assigned ${
                      unit.residents.filter((r) => !r.moveOutDate).length === 1
                        ? "resident"
                        : "residents"
                    }.`
                  : isPending
                  ? "This unit is pending resident assignment."
                  : "This unit currently has no residents assigned."}
              </p>
            </div>
            {!isEditing && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => handleOpenAddResidentDialog()}
                  className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md focus:outline-none ${
                    isOccupied
                      ? "text-green-700 bg-green-100 hover:bg-green-200"
                      : isPending
                      ? "text-yellow-700 bg-yellow-100 hover:bg-yellow-200"
                      : "text-gray-700 bg-gray-100 hover:bg-gray-200"
                  }`}
                >
                  {isOccupied ? "Add Another Resident" : "Assign Resident"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Unit Details */}
      <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Unit Information
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Details about this property unit.
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200">
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Name</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {isEditing ? (
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={editForm.name || ""}
                    onChange={handleInputChange}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  />
                ) : (
                  unit.name
                )}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Floor</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {isEditing ? (
                  <input
                    type="text"
                    name="floor"
                    id="floor"
                    value={editForm.floor || ""}
                    onChange={handleInputChange}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  />
                ) : (
                  unit.floor || "Not specified"
                )}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Size</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {isEditing ? (
                  <div className="relative rounded-md shadow-sm max-w-xs">
                    <input
                      type="number"
                      name="size"
                      id="size"
                      value={editForm.size || ""}
                      onChange={handleInputChange}
                      className="focus:ring-primary-500 focus:border-primary-500 block w-full pr-12 sm:text-sm border-gray-300 rounded-md"
                      placeholder="0"
                      step="0.01"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">m²</span>
                    </div>
                  </div>
                ) : unit.size ? (
                  `${unit.size} m²`
                ) : (
                  "Not specified"
                )}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Bedrooms</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {isEditing ? (
                  <input
                    type="number"
                    name="bedrooms"
                    id="bedrooms"
                    value={editForm.bedrooms || ""}
                    onChange={handleInputChange}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full max-w-xs sm:text-sm border-gray-300 rounded-md"
                    min="0"
                    step="1"
                  />
                ) : (
                  unit.bedrooms || "Not specified"
                )}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Bathrooms</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {isEditing ? (
                  <input
                    type="number"
                    name="bathrooms"
                    id="bathrooms"
                    value={editForm.bathrooms || ""}
                    onChange={handleInputChange}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full max-w-xs sm:text-sm border-gray-300 rounded-md"
                    min="0"
                    step="0.5"
                  />
                ) : (
                  unit.bathrooms || "Not specified"
                )}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Service Charge Share
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {isEditing ? (
                  <div className="flex items-center space-x-2 max-w-xs">
                    <input
                      type="number"
                      name="shareNumerator"
                      id="shareNumerator"
                      value={editForm.shareNumerator ?? 1}
                      onChange={handleInputChange}
                      className="focus:ring-primary-500 focus:border-primary-500 block w-20 sm:text-sm border-gray-300 rounded-md"
                      min="1"
                      step="1"
                      aria-label="Numerator"
                    />
                    <span className="text-gray-500 font-medium">/</span>
                    <input
                      type="number"
                      name="shareDenominator"
                      id="shareDenominator"
                      value={editForm.shareDenominator ?? 1}
                      onChange={handleInputChange}
                      className="focus:ring-primary-500 focus:border-primary-500 block w-20 sm:text-sm border-gray-300 rounded-md"
                      min="1"
                      step="1"
                      aria-label="Denominator"
                    />
                    <p className="text-xs text-gray-500">
                      e.g. 1/12 = divide the bill by 12
                    </p>
                  </div>
                ) : (
                  <span className="font-mono">
                    {unit.shareNumerator}/{unit.shareDenominator}
                  </span>
                )}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {isEditing ? (
                  <select
                    id="status"
                    name="status"
                    value={editForm.status || "vacant"}
                    onChange={handleInputChange}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full max-w-xs sm:text-sm border-gray-300 rounded-md"
                  >
                    <option value="occupied">Occupied</option>
                    <option value="vacant">Vacant</option>
                    <option value="pending">Pending</option>
                  </select>
                ) : (
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                    ${
                      unit.status === UnitStatus.Occupied
                        ? "bg-green-100 text-green-800"
                        : unit.status === "pending"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {unit.status.charAt(0).toUpperCase() + unit.status.slice(1)}
                  </span>
                )}
              </dd>
            </div>
            {/* Management Start Date (Bucket B #6). Optional. When the
                unit has no value the parent Building's date is used as
                fallback — surfaced as an italic hint in read mode so
                the treasurer sees at a glance whether they need to
                override it for this unit. */}
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Management Start Date
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {isEditing ? (
                  <>
                    <input
                      type="date"
                      id="managementStartDate"
                      name="managementStartDate"
                      value={editForm.managementStartDate || ""}
                      onChange={handleInputChange}
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md max-w-xs"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Leave empty to inherit the building's Management Start
                      Date (
                      {parentBuilding?.managementStartDate
                        ? new Date(
                            parentBuilding.managementStartDate,
                          ).toLocaleDateString()
                        : "not set — resident moves default to today"}
                      ).
                    </p>
                  </>
                ) : unit.managementStartDate ? (
                  new Date(unit.managementStartDate).toLocaleDateString()
                ) : (
                  <span className="text-gray-500 italic">
                    Inherited from building:{" "}
                    {parentBuilding?.managementStartDate
                      ? new Date(
                          parentBuilding.managementStartDate,
                        ).toLocaleDateString()
                      : "not set"}
                  </span>
                )}
              </dd>
            </div>
            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Notes</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {isEditing ? (
                  <textarea
                    id="notes"
                    name="notes"
                    rows={3}
                    value={editForm.notes || ""}
                    onChange={handleInputChange}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  />
                ) : (
                  unit.notes || "No notes provided"
                )}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Residents */}
      <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Current Residents
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              People currently occupying this unit.
            </p>
          </div>
          {!isEditing && (
            <button
              onClick={() => handleOpenAddResidentDialog()}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
            >
              Add Resident
            </button>
          )}
        </div>
        <div className="border-t border-gray-200">
          {unit.residents.filter((r) => !r.moveOutDate).length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {unit.residents
                .filter((r) => !r.moveOutDate)
                .map((resident) => (
                  <li key={resident.id} className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-gray-500">
                            <span className="font-medium leading-none text-white">
                              {resident.name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")}
                            </span>
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center">
                            <Link
                              to={`/dashboard/contacts/${resident.contactId}`}
                              className="text-sm font-medium text-primary-600 truncate hover:underline"
                            >
                              {resident.name}
                            </Link>
                            {resident.isPrimary && (
                              <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-primary-100 text-primary-800">
                                Primary
                              </span>
                            )}
                          </div>
                          <div className="mt-1 text-sm text-gray-500">
                            {resident.email}
                          </div>
                          <div className="mt-1 text-sm text-gray-500">
                            Moved in:{" "}
                            {new Date(resident.moveInDate).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div className="ml-2">
                        {!isEditing && (
                          <button
                            onClick={() => {
                              setSelectedResidentId(resident.id);
                              setShowRemoveResidentConfirm(true);
                            }}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none"
                          >
                            Move Out
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
            </ul>
          ) : (
            <div className="text-center py-8">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              <p className="mt-2 text-sm text-gray-500">
                No current residents assigned to this unit.
              </p>
              <div className="mt-3">
                <button
                  onClick={() => handleOpenAddResidentDialog()}
                  className="text-sm font-medium text-primary-600 hover:text-primary-500"
                >
                  Add a resident
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Resident History */}
      {unit.residents.some((r) => r.moveOutDate) && (
        <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Resident History
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Previous residents of this unit.
            </p>
          </div>
          <div className="border-t border-gray-200">
            <ul className="divide-y divide-gray-200">
              {unit.residents
                .filter((r) => r.moveOutDate)
                .map((resident) => (
                  <li
                    key={`${resident.id}-${resident.moveInDate}`}
                    className="px-4 py-4 sm:px-6"
                  >
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-gray-300">
                          <span className="font-medium leading-none text-gray-600">
                            {resident.name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")}
                          </span>
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-700">
                          {resident.name}
                        </div>
                        <div className="mt-1 text-sm text-gray-500">
                          {resident.email}
                        </div>
                        <div className="mt-1 text-xs text-gray-400">
                          {new Date(resident.moveInDate).toLocaleDateString()} –{" "}
                          {resident.moveOutDate
                            ? new Date(
                                resident.moveOutDate,
                              ).toLocaleDateString()
                            : ""}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      )}

      {/* Recent Statements */}
      {unit.recentStatements.length > 0 && (
        <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Recent Statements
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Latest service charge statements for this unit.
              </p>
            </div>
            <Link
              to={`/dashboard/statements?unitId=${unit.id}`}
              className="text-sm font-medium text-primary-600 hover:text-primary-500"
            >
              View All
            </Link>
          </div>
          <div className="border-t border-gray-200 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Period
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Amount
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Due Date
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Action</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {unit.recentStatements.map((statement) => (
                  <tr key={statement.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {statement.period}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      £{((statement.amount ?? 0) / 100).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${
                          statement.status === StatementStatus.Paid
                            ? "bg-green-100 text-green-800"
                            : statement.status === "overdue"
                            ? "bg-red-100 text-red-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {statement.status.charAt(0).toUpperCase() +
                          statement.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(statement.dueDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link
                        to={`/dashboard/statements/${statement.id}`}
                        className="text-primary-600 hover:text-blue-900"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteUnit}
        title="Delete Unit"
        message={`Are you sure you want to delete ${unit.name}? This action cannot be undone and will remove all associated data for this unit.`}
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />

      {/* Remove Resident Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showRemoveResidentConfirm}
        onClose={() => {
          setShowRemoveResidentConfirm(false);
          setSelectedResidentId(null);
        }}
        onConfirm={handleRemoveResident}
        title="Move Out Resident"
        message="Are you sure you want to record a move-out for this resident? They will be moved to the unit's resident history with today's date as their move-out date."
        confirmText="Remove"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />

      {/* Add Resident Dialog (placeholder - would be a more complex form in a real app) */}
      {showAddResidentDialog && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3
                    className="text-lg leading-6 font-medium text-gray-900"
                    id="modal-title"
                  >
                    Add Resident to Unit
                  </h3>
                  <div className="mt-4 space-y-4">
                    <div>
                      <label
                        htmlFor="contactId"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Select Resident Contact
                      </label>
                      {residentContacts.length === 0 ? (
                        <p className="mt-1 text-sm text-gray-500">
                          No available resident contacts. Create a contact with
                          type &ldquo;Resident&rdquo; first.
                        </p>
                      ) : (
                        <select
                          name="contactId"
                          id="contactId"
                          value={addResidentForm.contactId}
                          onChange={handleAddResidentFormChange}
                          className="mt-1 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        >
                          <option value="">-- Select a contact --</option>
                          {residentContacts.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                              {c.email ? ` (${c.email})` : ""}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="moveInDate"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Move-in Date
                      </label>
                      <input
                        type="date"
                        name="moveInDate"
                        id="moveInDate"
                        value={addResidentForm.moveInDate}
                        onChange={handleAddResidentFormChange}
                        className="mt-1 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Defaults to the effective Management Start Date for this
                        unit (
                        {new Date(
                          effectiveManagementStartDate,
                        ).toLocaleDateString()}
                        ). Adjust if the resident actually moved in on a
                        different date.
                      </p>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="isPrimary"
                        name="isPrimary"
                        type="checkbox"
                        checked={addResidentForm.isPrimary}
                        onChange={handleAddResidentFormChange}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="isPrimary"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        Set as primary resident
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleAddResident}
                  disabled={!addResidentForm.contactId}
                >
                  Add Resident
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                  onClick={() => setShowAddResidentDialog(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnitDetails;
